/**
 * Live browser viewer component using iframe for VNC-based task streaming
 */

import { useState, useEffect, useRef } from 'react'
import { Monitor, Maximize2, Minimize2, RefreshCw, AlertCircle, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { cn } from '../../lib/utils'

interface LiveBrowserViewerProps {
  liveUrl?: string
  taskId: string
  taskStatus: string
  className?: string
  onError?: (error: string) => void
}

type ViewerSize = 'small' | 'medium' | 'large' | 'fullscreen'

const VIEWER_SIZES = {
  small: { width: 640, height: 480 },
  medium: { width: 1024, height: 768 },
  large: { width: 1280, height: 960 },
  fullscreen: { width: '100%', height: '100vh' },
}

export function LiveBrowserViewer({ 
  liveUrl, 
  taskId, 
  taskStatus, 
  className,
  onError 
}: LiveBrowserViewerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [size, setSize] = useState<ViewerSize>('medium')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const isTaskActive = taskStatus === 'running' || taskStatus === 'queued' || taskStatus === 'active'
  const canShowViewer = liveUrl && (isTaskActive || taskStatus === 'paused')

  useEffect(() => {
    if (!canShowViewer) {
      setIsLoading(false)
      setHasError(false)
    }
  }, [canShowViewer])

  const handleIframeLoad = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const handleIframeError = () => {
    setIsLoading(false)
    setHasError(true)
    const error = 'Failed to load live browser session'
    setErrorMessage(error)
    onError?.(error)
  }

  const refreshViewer = () => {
    if (iframeRef.current && liveUrl) {
      setIsLoading(true)
      setHasError(false)
      // Force reload by changing src
      iframeRef.current.src = liveUrl + '?t=' + Date.now()
    }
  }

  const toggleFullscreen = async () => {
    if (!containerRef.current) return

    try {
      if (!isFullscreen) {
        await containerRef.current.requestFullscreen()
        setIsFullscreen(true)
        setSize('fullscreen')
      } else {
        await document.exitFullscreen()
        setIsFullscreen(false)
        setSize('medium')
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
      if (!document.fullscreenElement) {
        setSize('medium')
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  if (!canShowViewer) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Live Browser Session
          </CardTitle>
          <CardDescription>
            Real-time view of the browser automation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {!liveUrl 
                ? 'Live session not available for this task'
                : taskStatus === 'finished'
                ? 'Task has completed - live session ended'
                : taskStatus === 'failed'
                ? 'Task failed - live session unavailable'
                : 'Live session not available'
              }
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  const currentSize = VIEWER_SIZES[size]

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Card className={isFullscreen ? 'h-screen' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Live Browser Session
                <Badge variant={isTaskActive ? 'default' : 'secondary'}>
                  {taskStatus === 'active' ? 'Live' : isTaskActive ? 'Active' : 'Paused'}
                </Badge>
              </CardTitle>
              <CardDescription>
                Real-time VNC view of task execution
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {!isFullscreen && (
                <div className="flex items-center gap-1">
                  {(['small', 'medium', 'large'] as ViewerSize[]).map((sizeOption) => (
                    <Button
                      key={sizeOption}
                      variant={size === sizeOption ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSize(sizeOption)}
                      className="capitalize"
                    >
                      {sizeOption}
                    </Button>
                  ))}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={refreshViewer}
                disabled={isLoading}
              >
                <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="relative bg-gray-100 rounded-b-lg overflow-hidden">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span>Loading live session...</span>
                </div>
              </div>
            )}

            {hasError ? (
              <div className="flex items-center justify-center p-8">
                <Alert className="max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {errorMessage || 'Failed to load live browser session'}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshViewer}
                      className="mt-2 w-full"
                    >
                      Try Again
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                src={liveUrl}
                width={currentSize.width}
                height={currentSize.height}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                className={cn(
                  'border-0 bg-white',
                  isFullscreen ? 'w-full h-full' : 'mx-auto block'
                )}
                sandbox="allow-same-origin allow-scripts allow-forms allow-pointer-lock"
                allow="fullscreen"
                title={`Live browser session for task ${taskId}`}
              />
            )}
          </div>

          {/* Connection Status */}
          <div className="p-3 border-t bg-muted/50">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  !hasError && !isLoading ? 'bg-green-500' : 'bg-red-500'
                )} />
                <span className="text-muted-foreground">
                  {!hasError && !isLoading 
                    ? 'Connected to live session' 
                    : 'Connection failed'
                  }
                </span>
              </div>
              <span className="text-muted-foreground">
                Task ID: {taskId}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
